import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { useAnimatedScrollHandler, } from 'react-native-reanimated';
import { rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, } from 'design-system/values';
import useNotificationSpaceSwitch from 'features/spaces/hooks/useNotificationSpaceSwitch';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import ListLoader from 'design-system/ListLoader';
import ListEmptyView from 'design-system/ListEmptyView';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import SectionHeader from 'design-system/SectionHeader';
import useAppLink from 'hooks/useAppLink';
import { FlashList } from '@shopify/flash-list';
import { selectNotificationsListIsFetching, selectNotificationsListSections, selectNotificationsListSpacePage, selectNotificationsListSpacePageCount, } from '../selectors';
import { getNotifications } from '../actions';
import { isWeb } from '../../../constants';
import NotificationBadge from './NotificationBadge';
import NotificationItem from './NotificationItem';
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const keyExtractor = (item) => item.id.toString();
const getItemType = (item) => 'sectionTitle' in item ? 'sectionHeader' : 'row';
export const NotificationContainer = ({ scrollYs, space, unreadCount, onFirstNotificationLayout, scrollEnabled = true, }) => {
    const dispatch = useAppDispatch();
    useEffect(() => {
        dispatch(getNotifications(1, space.id));
    }, [dispatch, space.id]);
    const store = useAppStore();
    const notifications = useAppSelector((store) => selectNotificationsListSections(store, space.id));
    const isFetching = useAppSelector((store) => selectNotificationsListIsFetching(store, space.id));
    const shouldLoadSecondPage = useRef(false);
    const initialNotificationLength = useRef(notifications?.length);
    const loadMoreData = useCallback(() => {
        const page = selectNotificationsListSpacePage(store.getState(), space.id);
        const pageCount = selectNotificationsListSpacePageCount(store.getState(), space.id);
        if (page < pageCount) {
            dispatch(getNotifications(page + 1, space.id));
        }
    }, [space.id]);
    useEffect(() => {
        if (!isFetching && shouldLoadSecondPage.current) {
            shouldLoadSecondPage.current = false;
            loadMoreData();
        }
    }, [isFetching, loadMoreData]);
    const onEndReached = useCallback(() => {
        if (isFetching) {
            if (initialNotificationLength.current) {
                shouldLoadSecondPage.current = true;
            }
        }
        else {
            loadMoreData();
        }
    }, [isFetching, loadMoreData]);
    const emptyOrLoadingView = useMemo(() => isFetching ? (<ListLoader />) : (<View style={styles.sectionContainer}>
          <ListEmptyView happy onCard title="All clear!" subTitle="You have no notifications!"/>
        </View>), [isFetching]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyles = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, styles.contentContainerStyle, paddingHorizontalStyle);
    const renderHeader = useCallback(() => {
        if (isWeb) {
            return null;
        }
        return <NotificationBadge />;
    }, []);
    const { appLinkTo } = useAppLink();
    const confirmSpaceForNotification = useNotificationSpaceSwitch();
    const renderItem = useCallback(({ item, index }) => {
        if ('sectionTitle' in item) {
            return (<SectionHeader horizontalMargins style={styles.sectionHeader}>
            {item.sectionTitle}
          </SectionHeader>);
        }
        const shouldShowDot = item.index < unreadCount;
        const isTheFirstNotification = index === 1;
        // If the notification belongs to a different space, confirm + switch first, then follow the deep-link
        const onPress = async (appLink) => {
            const proceed = await confirmSpaceForNotification(item.data);
            if (proceed) {
                appLinkTo(appLink);
            }
        };
        return (<NotificationItem item={item} style={styles.notificationRow} onPress={onPress} showDot={shouldShowDot} onLayout={isTheFirstNotification ? onFirstNotificationLayout : undefined}/>);
    }, [
        appLinkTo,
        confirmSpaceForNotification,
        unreadCount,
        onFirstNotificationLayout,
    ]);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        // eslint-disable-next-line no-param-reassign
        scrollYs.value = {
            ...scrollYs.value,
            [space.id]: handle.contentOffset.y,
        };
    });
    const baseProps = {
        onScroll: scrollHandler,
        data: notifications,
        renderItem,
        scrollEventThrottle: 16,
        keyExtractor,
        onEndReached,
        onEndReachedThreshold: 0.1,
        ListHeaderComponent: renderHeader,
        ListEmptyComponent: emptyOrLoadingView,
        contentContainerStyle: contentContainerStyles,
        showsVerticalScrollIndicator: true,
        scrollIndicatorInsets: SCROLL_INDICATOR_INSETS,
        scrollEnabled,
    };
    return isWeb ? (<Animated.FlatList {...baseProps}/>) : (<AnimatedFlashList {...baseProps} getItemType={getItemType}/>);
};
const styles = StyleSheet.create({
    sectionContainer: {
        flex: 1,
        width: '100%',
    },
    contentContainerStyle: {
        paddingTop: rem(16),
    },
    sectionHeader: {
        marginBottom: rem(16),
    },
    notificationRow: {
        marginBottom: rem(16),
        marginHorizontal: rem(16),
    },
});
